import axios from "axios";
import React from "react";

import {
    NEWSLETTER_EXCLUDED_PAGES,
    NEWSLETTER_EXCLUDED_PRIMARY_TOPICS,
    NEWSLETTER_EXCLUDED_TAGS,
    JOB_ALERT_EXCLUDED_COMPANY_NAMES,
    JOB_ALERT_EXCLUDED_PAGES,
    JOB_ALERT_EXCLUDED_TAGS,
    JOB_ALERT_EXCLUDED_PRIMARY_TOPICS
} from "@advice/components/constants";
import { JobAlert } from "@advice/components/organisms/JobAlert";
import { Newsletter } from "@advice/components/organisms/Newsletter";
import { useSessionStorage } from "@advice/hooks/sessionStorage";
import { IInitialState } from "@server/util/pageWrapper";
import { hasTag } from "@util/tags";

const EmailCapture = (props: IInitialState) => {
    const [emailCaptureReady, setEmailCaptureReady] = React.useState(false);
    const [showNewsletter, setShowNewsletter] = React.useState(false);
    const [showJobAlert, setShowJobAlert] = React.useState(false);
    const [userCountry, setUserCountry] = useSessionStorage("carmen_country");
    const [userDMA, setUserDMA] = useSessionStorage("carmen_dma");
    const [userCity, setUserCity] = useSessionStorage("carmen_city");

    const { templateName, tags, sponsorShortName, slug, primaryTopic, companyShortName } = props;

    const shouldJobAlertShow = () => {
        // If carmen didn't resolve the user's location or their country is not the United States
        if (!userCountry || userCountry !== "United States") {
            return false;
        }

        switch (templateName) {
            case "companyLanding": {
                return !JOB_ALERT_EXCLUDED_COMPANY_NAMES.find(shortName => shortName === companyShortName);
            }
            case "landing": {
                return !JOB_ALERT_EXCLUDED_PAGES.find(excluded_slug => excluded_slug === slug);
            }
            case "listicle":
            case "article": {
                const foundCompany = JOB_ALERT_EXCLUDED_COMPANY_NAMES.find(shortName => shortName === sponsorShortName);
                if (foundCompany) {
                    return false;
                }

                // check tags
                for (const tag of JOB_ALERT_EXCLUDED_TAGS) {
                    if (hasTag(tags, tag)) {
                        return false;
                    }
                }

                const primaryTopicStr = typeof primaryTopic === "string" ? primaryTopic : primaryTopic?.slug;
                // check primary topic
                const foundPrimaryTopic = JOB_ALERT_EXCLUDED_PRIMARY_TOPICS.find(slug => slug === primaryTopicStr);
                if (foundPrimaryTopic) {
                    return false;
                }
                break;
            }
            default: {
                break;
            }
        }
        return true;
    };

    const shouldNewsletterShow = () => {
        switch (templateName) {
            case "companyLanding": {
                return true;
            }
            case "landing": {
                const foundSlug = NEWSLETTER_EXCLUDED_PAGES.some(x => x === slug);
                if (foundSlug) {
                    return false;
                }
                break;
            }
            case "listicle":
            case "article": {
                // check slugs
                const foundSlug = NEWSLETTER_EXCLUDED_PAGES.some(x => x === slug);
                // check tags
                const foundTag = NEWSLETTER_EXCLUDED_TAGS.some(x => hasTag(tags, x));
                const primaryTopicStr = typeof primaryTopic === "string" ? primaryTopic : primaryTopic?.slug;
                // check primary topic
                const foundPrimaryTopic = NEWSLETTER_EXCLUDED_PRIMARY_TOPICS.some(x => x === primaryTopicStr);
                if (foundSlug || foundTag || foundPrimaryTopic) {
                    return false;
                }
                break;
            }
            default: {
                break;
            }
        }
        return true;
    };

    React.useEffect(() => {
        if (!userCountry) {
            axios
                .get("https://carmen.themuse.com/me")
                .then(response => {
                    if (response.data.country) {
                        setUserCountry(response.data.country);
                    }
                    if (response.data.dma) {
                        setUserDMA(response.data.dma);
                    }
                    if (response.data.city) {
                        setUserCity(response.data.city);
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        }
        setShowNewsletter(shouldNewsletterShow());
        setShowJobAlert(shouldJobAlertShow());
        setEmailCaptureReady(true);
    }, [userCountry]);

    if (!emailCaptureReady) {
        return null;
    }

    if (showJobAlert) {
        return <JobAlert />;
    }

    if (showNewsletter) {
        return <Newsletter />;
    }

    return null;
};

export { EmailCapture };
